import type {
  CrowdexReferenceKind,
  CrowdexUIMessage,
  CrowdexUIMessagePart,
} from '@crowdex/agent-contracts';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@crowdex/ui/ai/conversation';
import type { InlineReferenceEditorHandle } from '@crowdex/ui/components/inline-reference-editor';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import {
  ConversationActionsProvider,
  ConversationMessageStream,
} from '@/components/conversation-pane-parts';
import type { AcpSelectConfigOption } from '@/components/desktop-config-controls';
import { ConversationComposer } from '@/components/desktop-shell/conversation-composer';
import { FailureBanner } from '@/components/desktop-shell/conversation-error-banners';
import { ErrorBanner } from '@/components/desktop-shell/error-banner';
import type { MentionTarget } from '@/components/desktop-side-panel';
import { useGroupConversation } from '@/features/plugins/plugin-conversation';
import { PluginWorkbenchContext } from '@/features/plugins/use-plugin-workbench';
import { useConversationRuntime } from '@/hooks/use-conversation-runtime';
import { useServerMessageRevision } from '@/hooks/use-message-sync';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { CachedConversation } from '@/lib/conversations/conversation-cache';
import {
  fallbackConversationTitle,
  normalizeReportedConversationTitle,
} from '@/lib/conversations/conversation-title';
import { conversationFailure, failureReason } from '@/lib/conversations/precondition-failure';
import { createComposerSubmit } from '@/lib/desktop-shell/composer-submit';
import { createConversationTurnControls } from '@/lib/desktop-shell/conversation-turn-controls';
import { useFollowUpDrain } from '@/lib/desktop-shell/follow-up-drain';
import { useResumedTranscript } from '@/lib/desktop-shell/pane-view-state';
import { desktopWorkspaceName } from '@/lib/desktop-shell/personal-workspace';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useConversationController } from '@/lib/desktop-shell/use-conversation-controller';
import { useFollowUpQueue } from '@/lib/desktop-shell/use-follow-up-queue';
import { isLocalMachine } from '@/lib/local-runtime';
import type { NativeAgent } from '@/lib/native-desktop';

const NO_COMPOSER_CONTEXT_TARGETS: readonly MentionTarget[] = [];

/** What an embedded conversation reports to the surface around it. */
export type EmbeddedConversationState = {
  messages: CrowdexUIMessage[];
  busy: boolean;
  ready: boolean;
  error: string | null;
  /** Sends a message as the user would (ignored until the conversation is ready). */
  send(text: string): void;
};

/**
 * A whole conversation — the same rich transcript and `ConversationComposer` as the primary pane
 * — for any surface that shows one beside other content: a split pane, a page's own chat. It
 * leaves out the workspace-level controls (cloud handoff, worktree), which belong to the primary
 * conversation. The transient composer menu state is read from the shared store (only one
 * composer is focused at a time); the draft is the caller's.
 */
export function EmbeddedConversation({
  agent,
  cloudRuntime,
  conversation,
  draft,
  header,
  onConversationChange,
  onDraftChange,
  onFocus,
  onStateChange,
  origin,
  preview,
  resolveReference,
}: {
  agent: NativeAgent | null;
  cloudRuntime: CloudRuntime | null;
  conversation: CachedConversation;
  draft: string;
  /** Shown above the transcript. */
  header?: () => ReactNode;
  onConversationChange: (conversation: CachedConversation) => void;
  onDraftChange: (value: string) => void;
  onFocus?: () => void;
  onStateChange?: (state: EmbeddedConversationState) => void;
  /** Who created the conversation when it is not the user (`plugin:<plugin>/<page>`). */
  origin?: string;
  preview: boolean;
  resolveReference?: (kind: CrowdexReferenceKind, id: string) => Promise<unknown>;
}) {
  const store = useDesktopShellStoreApi();
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  const queuedFollowUpsForConversation = useDesktopShellStore(
    (state) => state.queuedFollowUps[conversation.id],
  );
  const steeredFollowUpsForConversation = useDesktopShellStore(
    (state) => state.steeredFollowUps[conversation.id],
  );
  const queuedFollowUps = queuedFollowUpsForConversation ?? [];
  const steeredFollowUps = steeredFollowUpsForConversation ?? [];
  const followUpQueuePaused = useDesktopShellStore(
    (state) => state.pausedFollowUpQueues[conversation.id] !== undefined,
  );
  const modelId = conversation.modelId ?? null;
  // A split pane drawn again as the other half is shown where it was read.
  const resumed = useResumedTranscript(conversation.id);

  const { connection, chat, busy, stopGeneration } = useConversationRuntime({
    conversationId: conversation.id,
    agent: preview ? null : agent,
    cloudRuntime: preview ? null : cloudRuntime,
    workspaceRoot: preview ? null : conversation.workspaceRoot,
    restoredCollaboration: conversation.collaboration ?? null,
    preferredModelId: modelId ?? undefined,
    initialMessages: conversation.messages,
    persistenceTitle: conversation.title,
    persistenceWorkspace:
      conversation.workspaceRoot === null
        ? null
        : {
            name: desktopWorkspaceName(conversation.workspaceRoot, personalWorkspaceRoot),
            runtime: conversation.runtime === 'worktree' ? 'worktree' : 'local',
            ...(origin === undefined ? {} : { origin }),
          },
    resolveReference,
  });
  const { addToolApprovalResponse, messages, sendMessage, status, setMessages, error } = chat;
  const agentReady = preview || connection.status.state === 'ready';
  const ready = agentReady && !busy;
  const canSubmit = ready;
  const { removeQueuedFollowUp, enqueueFollowUp, reorderQueuedFollowUp, clearQueuedFollowUps } =
    useFollowUpQueue(store);
  const { sendQueuedFollowUp, steerFollowUp } = useFollowUpDrain({
    store,
    chatId: conversation.id,
    busy,
    agentReady,
    activeSteeredFollowUps: steeredFollowUps,
    activeQueuedFollowUps: queuedFollowUps,
    followUpQueuePaused,
    steer: async (message, messageId) =>
      message.files.length === 0 && (await connection.steerGeneration(message.text, messageId)),
    sendMessage,
    removeQueuedFollowUp,
    enqueueFollowUp,
  });

  const configOptions = connection.configOptions.filter(
    (option): option is AcpSelectConfigOption => option.type === 'select',
  );

  const composerRef = useRef<InlineReferenceEditorHandle>(null);
  const oppositeFollowUpModeRef = useRef(false);

  const onConversationChangeRef = useRef(onConversationChange);
  const conversationRef = useRef(conversation);
  onConversationChangeRef.current = onConversationChange;
  conversationRef.current = conversation;

  useEffect(() => {
    const firstUserText = messages
      .find((message) => message.role === 'user')
      ?.parts.find((part: CrowdexUIMessagePart) => part.type === 'text')?.text;
    const fallbackTitle = firstUserText
      ? fallbackConversationTitle(firstUserText)
      : conversationRef.current.title;
    const sessionTitle = [...messages].reverse().find((message) => message.metadata?.sessionTitle)
      ?.metadata?.sessionTitle;
    const title =
      sessionTitle === undefined
        ? conversationRef.current.title === 'New conversation'
          ? fallbackTitle
          : conversationRef.current.title
        : normalizeReportedConversationTitle(sessionTitle, fallbackTitle);
    onConversationChangeRef.current({
      ...conversationRef.current,
      title,
      collaboration: connection.collaboration,
      modelId: connection.modelId ?? undefined,
      messages,
      updatedAt: Date.now(),
    });
  }, [connection.collaboration, connection.modelId, messages]);

  const rememberServerRevision = useCallback(
    (saved: { revision: number; messageCount: number }) =>
      onConversationChangeRef.current({
        ...conversationRef.current,
        messageRevision: saved.revision,
        restoredMessageCount: saved.messageCount,
      }),
    [],
  );
  const serverSyncError = useServerMessageRevision({
    enabled: !preview && (cloudRuntime !== null || isLocalMachine()),
    settled: !busy,
    threadId: conversation.id,
    messageCount: messages.length,
    source: cloudRuntime === null ? 'local' : 'cloud',
    onLoaded: rememberServerRevision,
  });

  const connectionError = connection.status.state === 'error' ? connection.status.message : null;
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const sendRef = useRef((_text: string) => {});
  sendRef.current = (text) => {
    if (!preview && agentReady) void sendMessage({ text });
  };
  useEffect(() => {
    onStateChangeRef.current?.({
      messages,
      busy,
      ready: agentReady,
      error: connectionError,
      send: (text) => sendRef.current(text),
    });
  }, [messages, busy, agentReady, connectionError]);

  const turnControls = createConversationTurnControls({
    preview,
    busy,
    connection,
    modelConfigOptions: configOptions,
    // Its own chat, the only one this pane shows.
    conversation: { setMessages },
    setPreviewModel: () => undefined,
    setPreviewEffort: () => undefined,
    setPreferredModelId: (value) =>
      onConversationChangeRef.current({
        ...conversationRef.current,
        modelId: value ?? undefined,
        updatedAt: Date.now(),
      }),
  });
  const { changeModelConfig, changePlanFirst } = turnControls;

  const controller = useConversationController({
    store,
    composerRef,
    composerText: draft,
    composerContextTargets: NO_COMPOSER_CONTEXT_TARGETS,
  });

  const sendConversationAction = (text: string) => {
    if (preview) return;
    void sendMessage({ text });
  };

  const composerSubmit = createComposerSubmit({
    store,
    preview,
    busy,
    canSubmit,
    conversationId: conversation.id,
    editingMessageId: null,
    oppositeFollowUpModeRef,
    messages,
    setMessages,
    submitMessage: (message) => void sendMessage(message),
    enqueueFollowUp,
    removeQueuedFollowUp,
    steerFollowUp,
    prepareManagedWorktree: async () => false,
    pickerOpen:
      controller.commandQuery !== null ||
      controller.mentionQuery !== null ||
      controller.skillQuery !== null,
    cancelMessageEdit: () => undefined,
  });

  const GroupConversation = useGroupConversation(connection.collaboration);
  const composerElement = (
    <ConversationComposer
      agent={agent}
      agentReady={agentReady}
      availableSkills={connection.availableSkills}
      busy={busy}
      canSubmit={canSubmit}
      changeModelConfig={changeModelConfig}
      changePlanFirst={changePlanFirst}
      cloudAgentSelected={false}
      cloudConversationStartupPhase={null}
      cloudHandoffWorking={false}
      cloudWorkspacePanelActive={false}
      commandQuery={controller.commandQuery}
      composerRef={composerRef}
      composerText={draft}
      conversationId={conversation.id}
      collaboration={connection.collaboration}
      planFirst={connection.planFirst}
      editingMessageContainer={null}
      editingMessageId={null}
      insertArtifact={controller.insertArtifact}
      insertMention={controller.insertMention}
      insertSkill={controller.insertSkill}
      mentionTargets={controller.mentionTargets}
      mentionQuery={controller.mentionQuery}
      messages={messages}
      modelConfigOptions={configOptions}
      onToolApproval={addToolApprovalResponse}
      onClearFollowUps={() => clearQueuedFollowUps(conversation.id)}
      onDeleteFollowUp={(id) => removeQueuedFollowUp(conversation.id, id)}
      onKeyDown={composerSubmit.handleKeyDown}
      onReorderFollowUp={(draggedId, targetId) =>
        reorderQueuedFollowUp(conversation.id, draggedId, targetId)
      }
      onSteerFollowUp={sendQueuedFollowUp}
      onStop={() => void stopGeneration()}
      onSubmit={composerSubmit.handleSubmit}
      preview={preview}
      previewConversation={false}
      selectAgent={() => Promise.resolve()}
      setComposerText={(value) => onDraftChange(typeof value === 'function' ? value(draft) : value)}
      showAgentPicker={false}
      skillQuery={controller.skillQuery}
      status={status}
    />
  );
  return (
    <PluginWorkbenchContext value={connection.pluginWorkbench}>
      <ConversationActionsProvider
        busy={busy}
        messages={messages}
        onSend={sendConversationAction}
        ready={preview || connection.status.state === 'ready'}
        respondToInteraction={connection.respondToInteraction}
        setMessages={setMessages}
      >
        <section
          aria-label={`Conversation ${conversation.title}`}
          className="flex h-full min-h-0 min-w-0 flex-col bg-background"
          data-conversation-pane={conversation.id}
        >
          {header?.()}
          {connectionError !== null && (
            <FailureBanner
              agentName={agent?.name ?? null}
              failure={{
                message: connectionError,
                reason:
                  connection.status.state === 'error'
                    ? (connection.status.reason ?? failureReason(connectionError))
                    : null,
              }}
              layout={{ compact: true }}
            />
          )}
          {error !== undefined && (
            <FailureBanner
              agentName={agent?.name ?? null}
              failure={conversationFailure(error)}
              layout={{ compact: true }}
            />
          )}
          {serverSyncError !== null && (
            <ErrorBanner compact title="Conversation could not be saved.">
              {serverSyncError}
            </ErrorBanner>
          )}
          {GroupConversation ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <GroupConversation
                key={conversation.id}
                conversationId={conversation.id}
                initialMessages={messages}
                leadAgent={agent}
                workspaceRoot={conversation.workspaceRoot}
                cloud={cloudRuntime !== null}
                onMessagesChange={setMessages}
                composer={composerElement}
              />
            </div>
          ) : (
            <>
              <Conversation className="min-h-0 flex-1" onPointerDown={onFocus} {...resumed}>
                <ConversationContent className="mx-auto min-h-full w-full max-w-conversation gap-6 px-5 py-7">
                  <ConversationMessageStream
                    messageClassName="desktop-message"
                    messages={messages}
                    status={status}
                  />
                </ConversationContent>
                <ConversationScrollButton className="bottom-3 size-8 border-border bg-card shadow-lg" />
              </Conversation>
              <div className="shrink-0 bg-background px-4 pt-4 pb-3 sm:px-5">{composerElement}</div>
            </>
          )}
        </section>
      </ConversationActionsProvider>
    </PluginWorkbenchContext>
  );
}
